const LOWERCASE_WORDS_DISPLAY = new Set([
  "and", "or", "the", "in", "at", "of", "for", "to", "a", "an", "on", "by", "with",
]);

/**
 * Brands whose correct casing is neither all-caps nor Capitalized, mirrored from
 * `EXACT_CASE` in `shared/src/shared/text/name_casing.py`.
 *
 * These exist because some read paths case server-side and some do not: the
 * arbitrator's `profile_card` is already run through `titlecase_title`
 * (`ProfileService.get_profile_cards`), while the pipeline list returns the raw
 * column. Without this lookup, the all-caps rule below is the only thing that
 * preserves a token, so re-casing the server's output downgraded `McKinsey` to
 * `Mckinsey`, `PwC` to `Pwc` and `iOS` to `Ios` wherever both casers ran.
 *
 * Keep in sync with the Python dict. Every value is single-token, which is what
 * makes the lookup idempotent — each one re-lowercases to its own key.
 */
const EXACT_CASE: Record<string, string> = {
  ios: "iOS", iphone: "iPhone", ipad: "iPad", imac: "iMac", itunes: "iTunes",
  mckinsey: "McKinsey", deloitte: "Deloitte", jpmorgan: "JPMorgan", paypal: "PayPal",
  youtube: "YouTube", linkedin: "LinkedIn", github: "GitHub", stackoverflow: "StackOverflow",
  ebay: "eBay", fedex: "FedEx",
  javascript: "JavaScript", typescript: "TypeScript", postgresql: "PostgreSQL",
  mongodb: "MongoDB", graphql: "GraphQL", nodejs: "Node.js", nestjs: "NestJS",
  nextjs: "Next.js", reactjs: "React.js", vuejs: "Vue.js", mysql: "MySQL",
  openai: "OpenAI", chatgpt: "ChatGPT", devops: "DevOps", devsecops: "DevSecOps",
  powerbi: "PowerBI",
  ibm: "IBM", aws: "AWS", gcp: "GCP", sap: "SAP", pwc: "PwC", kpmg: "KPMG", ey: "EY",
};

/** Small words lowercased after the first token (filter autocomplete labels; broader set than display). */
const STRICT_TITLE_CASE_LOWERCASE_WORDS = new Set([
  "a", "an", "the", "and", "but", "or", "nor", "for", "in", "on", "at", "to", "by", "of", "up", "as", "is", "if", "it",
]);

/**
 * Title case for display strings; keeps all-caps tokens (e.g. acronyms) as-is
 * and leaves known mixed-case brands alone, so it is safe to run over a value
 * the backend has already cased.
 */
export function titleCasePreserveAcronyms(str: string): string {
  if (!str) return str;
  return str
    .split(/\s+/)
    .map((word, i) => {
      const exact = EXACT_CASE[word.toLowerCase()];
      if (exact) return exact;
      if (i > 0 && LOWERCASE_WORDS_DISPLAY.has(word.toLowerCase())) return word.toLowerCase();
      if (word === word.toUpperCase() && word.length > 1) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/** Title case for filter autocomplete labels; lowercases a broader set of small words and does not preserve acronyms. */
export function titleCaseStrictSmallWords(str: string): string {
  return str
    .split(" ")
    .map((word, i) => {
      if (!word) return word;
      const lower = word.toLowerCase();
      if (i > 0 && STRICT_TITLE_CASE_LOWERCASE_WORDS.has(lower)) return lower;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}
